import ReactGA from "react-ga4";
import { IntervalTab } from "src/components/IntervalTabs/enum/IntervalTab";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

interface ComponentState {
  selectedTab: IntervalTab;
  view?: string;
  selectedDate?: string;
}

interface IntervalTabsState {
  componentStates: Record<string, ComponentState>;
  setSelectedTab: (tab: IntervalTab, componentId: string) => void;
  setView: (componentId: string, view: string) => void;
  setDate: (componentId: string, date: string) => void;
  resetComponentState: (componentId: string) => void;
}

const DEFAULT_COMPONENT_STATE: ComponentState = {
  selectedTab: IntervalTab.Day,
  view: undefined,
  selectedDate: undefined,
};

const getComponentState = (componentStates: Record<string, ComponentState>, componentId: string): ComponentState =>
  componentStates[componentId] ?? DEFAULT_COMPONENT_STATE;

const useIntervalTabsStore = create<IntervalTabsState>()(
  persist(
    devtools((set) => ({
      componentStates: {},

      setSelectedTab: (tab: IntervalTab, componentId: string) => {
        ReactGA.event("intervalTabs_feature_usage", {
          feature: "selected_tab",
          tab,
          componentId,
        });

        set((state) => ({
          componentStates: {
            ...state.componentStates,
            [componentId]: {
              ...getComponentState(state.componentStates, componentId),
              selectedTab: tab,
            },
          },
        }));
      },

      setView: (componentId: string, view: string) => {
        ReactGA.event("intervalTabs_feature_usage", {
          feature: "set_view",
          componentId,
          view,
        });

        set((state) => ({
          componentStates: {
            ...state.componentStates,
            [componentId]: {
              ...getComponentState(state.componentStates, componentId),
              view,
            },
          },
        }));
      },

      setDate: (componentId: string, date: string) => {
        ReactGA.event("intervalTabs_feature_usage", {
          feature: "selected_date",
          componentId,
          date,
        });

        set((state) => ({
          componentStates: {
            ...state.componentStates,
            [componentId]: {
              ...getComponentState(state.componentStates, componentId),
              selectedDate: date,
            },
          },
        }));
      },

      resetComponentState: (componentId: string) => {
        ReactGA.event("intervalTabs_feature_usage", {
          feature: "reset",
          componentId,
          tab: IntervalTab.Day,
        });

        set((state) => ({
          componentStates: {
            ...state.componentStates,
            [componentId]: { ...DEFAULT_COMPONENT_STATE },
          },
        }));
      },
    })),
    {
      name: "interval-tabs-state",
      version: 2,
      migrate: (persistedState: unknown, version: number) => {
        if (version < 1) {
          return {}; // wipe — forces store to reinitialize with defaults
        }
        if (version < 2) {
          const state = persistedState as IntervalTabsState;
          const migratedComponentStates = Object.fromEntries(
            Object.entries(state.componentStates).map(([id, componentState]) => [
              id,
              {
                ...DEFAULT_COMPONENT_STATE,
                ...componentState,
              },
            ])
          );

          return {
            ...state,
            componentStates: migratedComponentStates,
          };
        }

        return persistedState;
      },
    }
  )
);

export default useIntervalTabsStore;
